/**
 * FreeAppsCallout — the apps that come free with every anchor, called out as free (APP-ENTITLEMENT-001; owner
 * ruling Q-AE-4: PalmPuffin and PrepPuffin are free with any anchor and the page says so).
 *
 * @module @puffin-core/marketing-shell/pricing/FreeAppsCallout
 */

import PropTypes from 'prop-types';
import { freeEntriesOf } from '@puffin-core/app-catalog';

import { SectionHeader } from '../components/SectionHeader.jsx';
import { useMarketingShellTranslate } from '../hooks/useMarketingShellTranslate.js';

/**
 * @param {Object} props
 * @param {import('./pricingRules.js').PublicCatalog} props.catalog
 * @param {string} props.anchorAppKey - the featured anchor whose free apps are listed.
 * @param {Function} [props.t]
 * @param {string} [props.id='free-apps']
 * @returns {React.JSX.Element|null} nothing when the anchor brings no free app.
 */
export function FreeAppsCallout({ catalog, anchorAppKey, t: hostT, id = 'free-apps' }) {
  const t = useMarketingShellTranslate(hostT);
  const free = freeEntriesOf(catalog, anchorAppKey);
  if (free.length === 0) return null;
  return (
    <section className="section-marketing section-light" id={id} aria-labelledby={`${id}-heading`}>
      <SectionHeader
        heading={t('pricing.freeAppsHeading')}
        subheading={t('pricing.freeAppsBody')}
        headingId={`${id}-heading`}
      />
      <ul className="tier-card-features" data-testid="free-apps">
        {free.map((app) => (
          <li key={app.appKey} className="tier-card-feature" data-testid={`free-app-${app.appKey}`}>
            <span className="tier-card-check" aria-hidden="true">
              ✓
            </span>
            <span>{app.label}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

FreeAppsCallout.propTypes = {
  catalog: PropTypes.shape({ apps: PropTypes.arrayOf(PropTypes.object).isRequired }).isRequired,
  anchorAppKey: PropTypes.string.isRequired,
  t: PropTypes.func,
  id: PropTypes.string,
};

export default FreeAppsCallout;
